'use client'

import { PublicationDraft } from '@/lib/publications/types'
import { publicationThemes } from '@/lib/publications/templates'

interface Props {
  draft: PublicationDraft
}

export default function TemplateSingleHero({ draft }: Props) {
  const theme = publicationThemes[draft.themeId]
  const post = draft.post

  if (!post) return null

  return (
    <div
      className={`relative aspect-square overflow-hidden rounded-[28px] ${theme.background} p-6`}
    >
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute -right-8 top-10 h-44 w-44 rounded-full bg-white/30 blur-3xl" />
        <div className="absolute -left-8 bottom-10 h-40 w-40 rounded-full bg-white/20 blur-3xl" />
      </div>

      <div className="relative flex h-full flex-col justify-between">
        <div className="flex items-start justify-between gap-4">
          <div className={`rounded-full px-3 py-1 text-[11px] font-semibold ${theme.badge}`}>
            {post.eyebrow || draft.brand?.name || 'RIMAN'}
          </div>

          <div className={`text-xs font-semibold tracking-[0.18em] uppercase ${theme.textSecondary}`}>
            {draft.brand?.name || 'RIMAN'}
          </div>
        </div>

        <div className="grid flex-1 grid-cols-2 gap-4 items-center">
          <div className="space-y-3">
            <h2 className={`text-[36px] leading-[1.05] font-serif ${theme.textPrimary}`}>
              {post.headline}
            </h2>

            {post.subheadline ? (
              <p className={`text-sm leading-6 ${theme.textSecondary}`}>
                {post.subheadline}
              </p>
            ) : null}

            {post.body ? (
              <p className={`text-sm leading-6 ${theme.textSecondary}`}>
                {post.body}
              </p>
            ) : null}

            {post.badges?.length ? (
              <div className="flex flex-wrap gap-2 pt-2">
                {post.badges.map((badge) => (
                  <span
                    key={badge}
                    className={`rounded-full px-3 py-1 text-[11px] font-medium ${theme.badge}`}
                  >
                    {badge}
                  </span>
                ))}
              </div>
            ) : null}
          </div>

          <div className="relative flex items-center justify-center">
            {post.productImageUrl ? (
              <img
                src={post.productImageUrl}
                alt={post.headline}
                className="max-h-[82%] max-w-[82%] object-contain drop-shadow-[0_20px_40px_rgba(0,0,0,0.14)]"
              />
            ) : (
              <div className="flex h-[78%] w-[78%] items-center justify-center rounded-[32px] bg-white/55 shadow-xl">
                <span className={`text-xs ${theme.textSecondary}`}>
                  Product image
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between">
          {post.cta ? (
            <div className={`rounded-full px-4 py-2 text-xs font-semibold ${theme.badge}`}>
              {post.cta}
            </div>
          ) : <div />}

          <div className={`text-[11px] ${theme.textSecondary}`}>
            {draft.brand?.name || 'Korean Skincare'}
          </div>
        </div>
      </div>
    </div>
  )
}